import cn from 'clsx'
import classnames from 'clsx'
import { mediaQueryUp } from 'components/utils/breakpoint'
import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { rem } from 'polished'

import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getAllPackagesWithIsBaseRate, OfferPackageOptionView } from 'selectors/offerPageSelectors'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import AlwaysOnPriceDetails from './AlwaysOnPriceDetails'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'

import { DMY_CASUAL_SHORT_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import TextButton from 'components/Luxkit/Button/TextButton'
import TextLink from 'components/Luxkit/TextLink'
import WalledContent from 'components/Common/WalledContent'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'
import OfferBookButton from '../OfferBookButton'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'
import Pill from 'components/Luxkit/Pill'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import Caption from 'components/Luxkit/Typography/Caption'
import { getDepositAmountPercentage, isDepositAvailableOnOffer, isDepositsEnabled } from 'lib/depositsUtils'
import GeoContext from 'contexts/geoContext'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import getValueOffPercent from 'lib/getValueOffPercent'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import { getOnlyNRoomLeftLabel } from 'lib/getUrgencyLabels'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import OfferPackagePartnershipLabel from 'components/Common/Labels/OfferPackagePartnershipLabel'
import { VIRGIN_VELOCITY_PARTNERSHIP_CODE } from 'constants/partnerships'
import renderInclusions from './inclusions/renderInclusions'
import useOfferAvailableRates from 'hooks/useOfferAvailableRates'
import config from 'constants/config'
import { isFlightForceBundled } from 'lib/offer/isFlightForceBundled'
import { useOptimizelyExperiment, OptimizelyClientSideExperiments } from 'lib/optimizely'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import { pluralizeToString } from 'lib/pluralize'

const ContentGrid = styled.div`
  display: grid;
  grid-gap: ${rem(16)};
  grid-template-columns: 1fr;
  padding: ${rem(16)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr ${rem(392)};
    grid-template-rows: min-content 1fr;
    grid-column-gap: ${rem(24)};
    grid-row-gap: ${rem(8)};

    &.expanded {
      grid-template-columns: 1fr ${rem(384)};
    }
  }

  ${mediaQueryUp.desktop} {
    padding: ${rem(18)} ${rem(32)};
    grid-template-columns: ${rem(256)} 1fr ${rem(372)};

    &.expanded {
      grid-template-columns: ${rem(256)} 1fr ${rem(372)};
    }
  }
`

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${mediaQueryUp.tablet} {
    grid-column: 1;
  }

  ${mediaQueryUp.desktop} {
    text-align: left;
    grid-column: 1;
  }
`

const InclusionsSection = styled(BodyText)`
  display: flex;
  flex-direction: column;

  grid-column: 1;
  grid-row: 2;

  ${mediaQueryUp.tablet} {
    align-items: flex-start;
    grid-column: 1;
    grid-row: 2;
  }

  ${mediaQueryUp.desktop} {
    grid-column: 2;
    grid-row: 1;
  }
`

const BookingSection = styled.div`
  display: flex;
  justify-content: space-between;
  grid-column: 1;
  grid-row: 3;
  align-items: flex-end;

  > * + * {
    margin-left: ${rem(16)};
  }

  ${mediaQueryUp.tablet} {
    grid-column: 2;
    grid-row: 1 / span 2;
    align-items: flex-start;
    justify-content: flex-end;
  }

  ${mediaQueryUp.desktop} {
    grid-column: 3;
    grid-row: 1;
  }
`

const PriceText = styled(Caption)`
  position: relative;
`

const PackagePrices = styled(BodyText)`
  display: flex;
  position: relative;

  > * {
    flex: 1 1 50%;
    padding-right: ${rem(8)};
  }

  > * + * {
    padding-left: ${rem(8)};
    border-left: 1px solid ${props => props.theme.palette.neutral.five};
    margin-left: ${rem(8)};
  }

  &.show_discount {
    margin-top: ${rem(8)};
  }
`
const PackagePrice = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  gap: ${rem(8)};
  min-width: ${rem(144)};
  max-height: ${rem(192)};
`

const HotelAndFlightText = styled(Caption)`
  padding-left: ${rem(32)};
`
const HotelAndFlightIcon = styled(LinePlaneFlyIcon)`
  position: absolute;
  left: ${rem(8)};
  top: ${rem(-4)};
`

const ChangeDepartureWrapper = styled.div`
  margin-bottom: auto;
  margin-top: ${rem(20)};
`

const TagWrapper = styled.div`
  display: flex;
  gap: ${rem(5)};

  ${mediaQueryUp.tablet} {
    flex-direction: column;
  }
`

const StyledLabelGroup = styled(LabelGroup)`
  margin-top: ${rem(8)};
`

const StyledOfferPackagePartnershipLabel = styled(OfferPackagePartnershipLabel)`
  margin-top: ${rem(12)};
`

const TravelPeriodSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${rem(8)};
`

interface Props {
  optionNumber: number;
  offer: App.Offer;
  packageView: OfferPackageOptionView;
  partnership: App.Partnership;
  checkIn: moment.Moment;
  duration: number;
  rooms: Array<App.Occupants>;
  calendarsLoading?: boolean;
  flightOrigin?: App.Airport;
  flightDestination?: App.Airport;
  previewMode: boolean;
  currency: string;
  packagesWithIsBaseRate: { duration: number, value: number, packageId: string }[]
  defaultDepositAmountPercentage: number;
}

function AlwaysOnRateOption(props: Props) {
  const {
    offer,
    packageView,
    partnership,
    checkIn,
    duration,
    rooms,
    optionNumber,
    calendarsLoading,
    flightOrigin,
    flightDestination,
    previewMode,
    currency,
    packagesWithIsBaseRate,
    defaultDepositAmountPercentage,
  } = props

  const {
    package: pkg,
    hasTactical,
    flightPrice,
    bundledDiscountPercent,
    hotelDiscountPercent,
  } = packageView

  const offerPageDispatch = useOfferPageDispatch()

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.setRoom, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterTypes })
  }, [offerPageDispatch, pkg.roomType])

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const availableForPurchase = useIsAvailableForPurchase()
  const checkOut = checkIn ? checkIn.clone().add(pkg.duration, 'days') : undefined

  const offerAvailableRates = useOfferAvailableRates(offer?.id, {
    checkIn: checkIn?.format(ISO_DATE_FORMAT),
    checkOut: checkOut?.format(ISO_DATE_FORMAT),
    rooms,
  })

  const pkgAvailableRate = useMemo(() => offerAvailableRates?.rates?.find(rate => rate.packageUniqueKey === pkg.uniqueKey), [pkg, offerAvailableRates])
  const fetching = !!calendarsLoading || !!offerAvailableRates?.fetching
  const useDynamicCancellationPolicies = offer.property.useDynamicCancellationPolicies ?? false
  const hasFlights = !!flightPrice && !offer.flightCacheDisabled

  const dynamicHotelAvailable = ((useDynamicCancellationPolicies && !!pkgAvailableRate && checkIn && !fetching) || !useDynamicCancellationPolicies || !checkIn || fetching)
  const hotelAvailable = packageView.hotelAvailable && dynamicHotelAvailable
  const canPurchase = availableForPurchase && packageView.capacityValid && hotelAvailable
  const canBuyNow = !!checkIn && !!rooms && canPurchase && !fetching && packageView.fromCalendarInfo
  const isValidRoomOccupancy = packageView.flightsAvailable || offer.bundledWithFlightsOnly
  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && !(hotelAvailable && !packageView.capacityValid && availableForPurchase && !canPurchase) && isValidRoomOccupancy

  const departureTooEarly = useMemo(() => {
    const minDay = 1
    return !!checkIn && checkIn.diff(moment(), 'days') < minDay
  }, [checkIn])

  const basePackageDuration = useMemo(() => {
    return packagesWithIsBaseRate.find(
      (packageItem) => packageItem.duration === pkg.duration && packageItem.packageId === pkg.id,
    )
  }, [packagesWithIsBaseRate, pkg.duration, pkg.id])

  const baseRateCost = basePackageDuration?.value

  const refundable = !useDynamicCancellationPolicies && pkg.roomRate?.cancellationPolicy?.type !== 'non-refundable'

  const departureTooFarInFuture = useMemo(() => checkIn && checkIn.diff(moment.utc(), 'days') > 330, [checkIn])

  const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const highestDiscountPercent = hotelDiscountPercent > bundledDiscountPercent ? hotelDiscountPercent : bundledDiscountPercent
  const shouldShowDiscountWrapper = (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && (canPurchase || previewMode) && !pkg.roomRate?.inclusionsHideValue
  const { showTaxExclusivePrices } = useContext(GeoContext)

  // We use base rate cost to calculate new totalValue and discountPercent, using the isBaseRate flag on rate plan level.
  // This shows a bigger discount
  const valuedUpTo = useMemo(() => {
    if (baseRateCost && (baseRateCost + pkg.inclusionsAmount) > pkg.value) {
      const displayedHotelValue = baseRateCost + pkg.inclusionsAmount + packageView.propertyFees
      const displayedHotelDiscountPercent = getValueOffPercent(displayedHotelValue, packageView.hotelPrice)

      if (displayedHotelDiscountPercent > highestDiscountPercent) {
        return { value: displayedHotelValue, percent: displayedHotelDiscountPercent }
      }
    }

    return { value: packageView.hotelValue, percent: highestDiscountPercent }
  }, [baseRateCost, pkg.inclusionsAmount, pkg.value, packageView, highestDiscountPercent])

  let totalPrice = packageView.hotelPrice
  totalPrice = showTaxExclusivePrices ? totalPrice : totalPrice + packageView.propertyFees

  const isDepositEnabled = isDepositAvailableOnOffer(offer, currency, checkIn, totalPrice, packageView.package, pkgAvailableRate)

  const { inclusions, bonusInclusions, tacticalInclusions } = useOfferInclusions(pkg, checkIn, checkIn?.clone().add(duration, 'days'))

  const urgencyLabel = useMemo(() => {
    return getOnlyNRoomLeftLabel(packageView.availableRooms, true, config.RATE_OPTION_ONLY_N_ROOMS_LEFT_THRESHOLD)
  }, [packageView.availableRooms])

  const showPartnership = partnership?.code === VIRGIN_VELOCITY_PARTNERSHIP_CODE && !!packageView.hotelAvailable && !!partnership?.hasEarn

  const shouldShowLoggedOutPrice = !!useOptimizelyExperiment(OptimizelyClientSideExperiments.searchShowLoggedOutPrices)

  const { travelFrom, travelTo } = useMemo(() => {
    const tacticalSchedule = pkg?.roomRate?.schedules?.find(x => x.type === 'tactical')
    let travelFrom: string|undefined = tacticalSchedule?.travelPeriod?.from
    let travelTo: string|undefined = tacticalSchedule?.travelPeriod?.to
    if ((!travelFrom || !travelTo) && tacticalInclusions.length > 0) {
      const tacticalInclusionsSchedule = tacticalInclusions[0].schedules?.find(x => x.type === 'tactical')
      travelFrom = tacticalInclusionsSchedule?.travelPeriod?.from
      travelTo = tacticalInclusionsSchedule?.travelPeriod?.to
    }

    return { travelFrom, travelTo }
  }, [pkg?.roomRate?.schedules, tacticalInclusions])

  return (
    <ContentGrid className={cn({ expanded: !!checkIn })} data-testid={pkg.uniqueKey}>
      <TitleSection>
        <BodyText variant="small" colour="neutral-two">Option {optionNumber}</BodyText>
        {hasTactical && isLPCTacticalEnabled() && <div>
          <ProductTypeLabel productType="limited_time_special"/>
        </div>}
        <BodyText variant="medium" weight="bold">{packageView.package.name}</BodyText>
        <TAOCancellationPolicies
          offerId={offer.id}
          useDynamicCancellationPolicies={offer.property?.useDynamicCancellationPolicies}
          timezoneOffset={offer.property?.timezoneOffset}
          uniqueKey={pkg.uniqueKey}
          checkInDate={checkIn ? checkIn.format(ISO_DATE_FORMAT) : undefined}
          checkOutDate={checkIn ? checkIn.clone().add(duration, 'days').format(ISO_DATE_FORMAT) : undefined}
          occupants={rooms}
          cancellationPolicy={pkg?.roomRate?.cancellationPolicy}
        />
        {!!showPartnership && partnership.code !== VIRGIN_VELOCITY_PARTNERSHIP_CODE &&
          <StyledOfferPackagePartnershipLabel
          offer={offer}
          price={packageView.hotelPrice}
        />}
        {urgencyLabel && <StyledLabelGroup>
          <UrgencyLabel variant="critical1">{urgencyLabel}</UrgencyLabel>
        </StyledLabelGroup>}
        {hasTactical && isLPCTacticalEnabled() && <TravelPeriodSection>
          <BodyText variant="small" weight="normal" colour="neutral-two">
            {travelFrom && travelTo && (
              <>
                Travel between: {moment(travelFrom).format(DMY_CASUAL_SHORT_FORMAT)} - {moment(travelTo).subtract(pkg.duration, 'days').format(DMY_CASUAL_SHORT_FORMAT)}.
                {' '}
              </>
            )}
            Minimum stay: {pluralizeToString('night', pkg.duration)}
          </BodyText>
        </TravelPeriodSection>}
      </TitleSection>
      <InclusionsSection variant="small">
        <LargeContentToggle
          maxHeight={208}
          showMoreText="View all inclusions"
          showLessText="View less inclusions"
        >
          {renderInclusions([...tacticalInclusions, ...bonusInclusions], inclusions)}
        </LargeContentToggle>
      </InclusionsSection>
      <BookingSection>
        <WalledContent enforced={shouldShowLoggedOutPrice ? false : offer.walledGarden}>
          <div>
            <TagWrapper>
              {shouldShowDiscountWrapper &&
                <ValueWrapper variant="medium" weight="bold">
                  <Pill kind="primary" variant="success">Save up to {valuedUpTo.percent}%</Pill>
                </ValueWrapper>
              }
              {isDepositEnabled &&
                <SecureWithDepositTagAndModal pkgAvailableRate={pkgAvailableRate} offerType={offer.type} depositAmountPercentage={depositAmountPercentage} depositThresholds={offer.depositThresholds}/>
              }
            </TagWrapper>

            <PackagePrices variant="small" className={classnames({ show_discount: (shouldShowDiscountPercent || shouldShowBundledDiscountPercent || isDepositsEnabled) })}>
              {!offer.bundledWithFlightsOnly && <PackagePrice data-testid="pricing">
                {(canPurchase || previewMode) && (
                  <div>
                    <Caption variant="medium" weight="bold">Hotel</Caption>
                    <PriceText variant="medium" data-testid="pricing">
                      <AlwaysOnPriceDetails
                        rooms={rooms}
                        checkIn={checkIn ? checkIn.format(ISO_DATE_FORMAT) : null}
                        packageView={packageView}
                        offer={offer}
                        canBuyNow={canBuyNow}
                        valuedUpTo={valuedUpTo}
                      />
                      <LoadingIndicator visible={fetching} floating opaque />
                    </PriceText>
                  </div>
                )}
                {canBuyNow && (
                  <OfferBookButton
                    type="submit"
                    disabled={fetching}
                    data-testid={cn('HotelBookingBtn', 'buy-now-button', { refundable })}
                    pkg={packageView.package}
                  >
                    Book now
                  </OfferBookButton>
                )}
                {availableForPurchase && !canPurchase && (
                  <div>
                    {hotelAvailable && !packageView.capacityValid && (
                      <Caption variant="large" align="center">
                        Max number of guests exceeded
                        <br />
                        {!isFlightForceBundled(offer) && (<TextButton
                          kind="tertiary"
                          onClick={onToggleFilter}
                          data-filter="capacity"
                        >
                          Add another {offer.saleUnit}
                        </TextButton>)}
                      </Caption>
                    )}
                    {!hotelAvailable && (
                      <Caption variant="large">
                        Sold out for your selected dates.
                      </Caption>
                    )}
                  </div>
                )}
              </PackagePrice>}
              {flightsEnabled && hotelAvailable && (
                <PackagePrice data-testid="pricing">
                  <div>
                    <HotelAndFlightText variant="medium" weight="bold">
                      <HotelAndFlightIcon /> Hotel + flights
                    </HotelAndFlightText>
                    {!hasFlights && flightOrigin.code === flightDestination?.code && (
                      <ChangeDepartureWrapper>
                        <BodyText variant="small" colour="neutral-two">
                          No flights available from {flightOrigin.name}. Please select another airport.
                        </BodyText>
                        <TextLink
                        onClick={onToggleFilter}
                        data-filter="airport"
                      >
                          Change departure airport
                        </TextLink>
                      </ChangeDepartureWrapper>
                    )}
                    {!hasFlights && flightOrigin.code !== flightDestination?.code && !departureTooFarInFuture && !departureTooEarly && (
                      <BodyText variant="small" colour="neutral-two">
                        Add flights to the hotel package. Select from all major airlines.
                      </BodyText>
                    )}
                    {!hasFlights && departureTooFarInFuture && (
                      <ChangeDepartureWrapper>
                        <BodyText variant="small" colour="neutral-two">
                          Flights can only be booked up to 11 months in advance.
                        </BodyText>
                      </ChangeDepartureWrapper>
                    )}
                    {flightOrigin.code !== flightDestination?.code && departureTooEarly && (
                      <ChangeDepartureWrapper>
                        <BodyText variant="small" colour="neutral-two">
                          Flights cannot be booked online when check-in is within 24 hours
                        </BodyText>
                        <TextLink
                        onClick={onToggleFilter}
                        data-filter="checkin"
                      >
                          Change departure date
                        </TextLink>
                      </ChangeDepartureWrapper>
                    )}
                  </div>
                  {((hasFlights && !departureTooEarly) || (hasFlights && previewMode)) && (
                    <PriceText variant="medium" data-testid="pricing">
                      <AlwaysOnPriceDetails
                        rooms={rooms}
                        checkIn={
                          checkIn ? checkIn.format(ISO_DATE_FORMAT) : null
                        }
                        packageView={packageView}
                        offer={offer}
                        canBuyNow={canBuyNow}
                        isFlightPrice
                        valuedUpTo={valuedUpTo}
                      />
                      <LoadingIndicator visible={fetching} floating opaque />
                    </PriceText>
                  )}
                  <AtolProtectedTooltip />
                  {canBuyNow && flightOrigin.code !== flightDestination?.code && !departureTooEarly && (
                    <OfferBookButton
                      disabled={fetching}
                      data-testid="HotelAndFlightsBtn"
                      data-buytype="flights"
                      pkg={packageView.package}
                    >
                      Book now
                    </OfferBookButton>
                  )}
                  {availableForPurchase && !canPurchase && (
                    <div>
                      {!hotelAvailable && (
                        <BodyText variant="medium">
                          Sold out for your selected dates.
                        </BodyText>
                      )}
                    </div>
                  )}
                </PackagePrice>
              )}
            </PackagePrices>
          </div>
        </WalledContent>
      </BookingSection>
    </ContentGrid>
  )
}

export default withOfferPageAndReduxState((state, appState, ownProps: Partial<Props>) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  duration: state.duration,
  partnership: appState.geo.partnership,
  calendarsLoading: !!appState.calendar.calendarsLoading[ownProps.packageView.package.uniqueKey],
  previewMode: appState.config.previewMode,
  flightOrigin: state.airport,
  currency: appState.geo.currentCurrency,
  packagesWithIsBaseRate: getAllPackagesWithIsBaseRate(appState, state),
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
}))(AlwaysOnRateOption)
